import { useQuery } from '@tanstack/react-query';

import { trackerScopeQueryOptions, type TrackerScope } from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/**
 * The rows endpoint's `scope` parameter for a stored scope. It is sent
 * explicitly so the rows query key changes with the scope. A stored selection
 * is sent as its readable part: an explicit list nobody can read fails,
 * where the stored one falls back to every workspace. With no scope to read,
 * it is left out and the server applies the stored one.
 */
export function rowsScopeParam(scope: TrackerScope | undefined): string | undefined {
  if (!scope) return undefined;
  const stored = new Set(scope.workspaceIds);
  const selected = scope.resolvedWorkspaceIds.filter((id) => stored.has(id));
  // eslint-disable-next-line i18next/no-literal-string -- an API scope value
  return selected.length > 0 ? selected.join(',') : 'all';
}

export interface WorkspaceScope {
  workspaceId: string;
  scope: TrackerScope | undefined;
  rowsScope: string | undefined;
  /** Rows may come from more than one workspace, so they carry `workspace`. */
  readsSeveral: boolean;
  /**
   * The scope has an answer, or failed to get one. Until then the rows query
   * waits, so it does not fetch once without the scope and again with it.
   */
  settled: boolean;
}

export function useWorkspaceScope(trackerId: string): WorkspaceScope {
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const query = useQuery(trackerScopeQueryOptions(workspaceId, trackerId));
  return {
    workspaceId,
    scope: query.data,
    rowsScope: rowsScopeParam(query.data),
    // An unread scope leaves the server on the stored one, which may read several workspaces.
    readsSeveral: query.data ? query.data.resolvedWorkspaceIds.length > 1 : query.isError,
    settled: !query.isPending,
  };
}
